import React, { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/contexts/auth-context'
import { CORPORATE_ENROLLMENT_QUERY_KEY } from '@/constants/corporateQueryKeys'
import { usePageStateStore } from '@/reducers'
import { useCorpStore } from '@/reducers/corporateReducer'
import { RequestError } from '@/requests/corporate'
import { CORPORATE_LOGIN_PATH } from '@/constants/corporateRoutePaths'
import { useCorporateEnrollment } from '@/hooks/useCorporateEnrollment'
import {
  getCorporateResumeStep,
  isStepReachable,
} from '@/utils/corporateResume'
import { LoadingIcon } from './LoadingIcon'

const CorporateGateInner = () => {
  const { isAuth, loading: isAuthLoading } = useAuth()
  const location = useLocation()
  const {
    data: enrollment,
    isLoading: isEnrollmentLoading,
    isError,
    error,
    refetch,
  } = useCorporateEnrollment(isAuth)
  const clearAuthToken = usePageStateStore.use.clearAuthToken()
  const resetEnrollmentForm = useCorpStore((state) => state.reset)
  const queryClient = useQueryClient()

  const isSessionExpired =
    isError && error instanceof RequestError && error.status === 401

  useEffect(() => {
    if (!isSessionExpired) return
    clearAuthToken()
    resetEnrollmentForm()
    queryClient.removeQueries({ queryKey: CORPORATE_ENROLLMENT_QUERY_KEY })
  }, [isSessionExpired, clearAuthToken, resetEnrollmentForm, queryClient])

  if (isSessionExpired) {
    return (
      <Navigate to={CORPORATE_LOGIN_PATH} replace state={{ from: location }} />
    )
  }

  if (isAuthLoading) return <LoadingIcon />

  if (!isAuth) {
    return (
      <Navigate to={CORPORATE_LOGIN_PATH} replace state={{ from: location }} />
    )
  }

  if (isEnrollmentLoading) return <LoadingIcon />

  if (isError) {
    return (
      <div
        className="my-auto flex flex-col items-center justify-center gap-4 p-5 text-center"
        data-testid="container-corporategate-error"
      >
        <p className="text-base">
          We couldn&apos;t load your corporate application. Please try again.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="underline"
          data-testid="btn-corporategate-retry"
        >
          Try Again
        </button>
      </div>
    )
  }

  const resumeStep = getCorporateResumeStep(enrollment ?? null, isAuth)
  const shouldRedirect =
    resumeStep !== null &&
    resumeStep !== location.pathname &&
    !isStepReachable(location.pathname, enrollment ?? null)

  if (shouldRedirect) return <Navigate to={resumeStep} replace />

  return <Outlet />
}

export const CorporateGate = React.memo(CorporateGateInner)
